import { PushNotifications, type Channel } from '@capacitor/push-notifications';
import { ANDROID_CHANNEL_MESSAGES, ANDROID_CHANNEL_NEW_RIDE_REQUESTS, ANDROID_CHANNELS_OBSOLETE, ANDROID_CHANNEL_REMINDERS, ANDROID_CHANNEL_RIDE_UPDATES } from '@tcrd/shared';
import { api } from './api';
import { env } from './env';

/** Canales de Android 8+. Nuevas solicitudes: importancia máxima (heads-up, sonido del sistema y vibración). */
const CHANNELS: Channel[] = [
  { id: ANDROID_CHANNEL_NEW_RIDE_REQUESTS, name: 'Nuevas solicitudes de viaje', description: 'Alertas urgentes de nuevas solicitudes y reservas', importance: 5, visibility: 0, vibration: true, lights: true, lightColor: '#E5252A' },
  { id: ANDROID_CHANNEL_RIDE_UPDATES, name: 'Estado de viajes', description: 'Conductor en camino, llegada, inicio y fin del viaje, cotizaciones y pagos', importance: 4, visibility: 0, vibration: true },
  { id: ANDROID_CHANNEL_MESSAGES, name: 'Mensajes', description: 'Chat con tu conductor o cliente', importance: 4, visibility: 0, vibration: true },
  { id: ANDROID_CHANNEL_REMINDERS, name: 'Recordatorios', description: 'Reservas próximas y vencimiento de documentos', importance: 3, visibility: 0 },
];

let registered = false;
let currentToken: string | null = null;

/** Registro de push en Android (FCM). Solicita POST_NOTIFICATIONS en Android 13+. */
export async function registerNativePush(onTap: (data: Record<string, string>) => void, onForeground: (title: string, data: Record<string, string>) => void) {
  if (!env.isNative || registered) return;
  registered = true;
  for (const id of ANDROID_CHANNELS_OBSOLETE) await PushNotifications.deleteChannel({ id }).catch(() => undefined);
  for (const c of CHANNELS) await PushNotifications.createChannel(c).catch(() => undefined);

  await PushNotifications.addListener('registration', async (t) => {
    currentToken = t.value;
    await api('/notifications/devices', { body: { platform: 'ANDROID', token: t.value, deviceName: navigator.userAgent.slice(0, 100) } }).catch(() => undefined);
  });
  await PushNotifications.addListener('registrationError', (e) => console.warn('Push no disponible:', e.error));
  await PushNotifications.addListener('pushNotificationActionPerformed', (a) => onTap((a.notification.data ?? {}) as Record<string, string>));
  await PushNotifications.addListener('pushNotificationReceived', (n) => onForeground(n.title ?? 'TuConductorRD', (n.data ?? {}) as Record<string, string>));

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') return;
  await PushNotifications.register();
}

export async function unregisterNativePush() {
  if (!env.isNative || !currentToken) return;
  await api('/notifications/devices', { method: 'DELETE', body: { token: currentToken } }).catch(() => undefined);
  await PushNotifications.unregister().catch(() => undefined);
  registered = false;
  currentToken = null;
}

function b64ToUint8(base64: string) {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/** Web Push en navegadores compatibles (requiere VAPID configurado en el servidor). */
export async function enableWebPush(): Promise<'enabled' | 'unsupported' | 'denied' | 'not-configured'> {
  if (env.isNative || !('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  const { publicKey } = await api<{ publicKey: string | null }>('/notifications/web-push-key');
  if (!publicKey) return 'not-configured';
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return 'denied';
  const reg = await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(publicKey) }));
  const json = sub.toJSON();
  await api('/notifications/devices', { body: { platform: 'WEB', token: json.endpoint, subscription: json, deviceName: navigator.userAgent.slice(0, 100) } });
  return 'enabled';
}
